import {makeObservable, type IReactionDisposer, action, observable} from 'mobx';
import {nanoid} from 'nanoid';
import {SearchType} from './constants';
import type {SearchRuleBase, RuleValidationErrors} from './types';

export abstract class RuleModel<T extends SearchType = SearchType> {
	abstract get isValid(): boolean;

	elementValue1Label = '';
	elementValue2Label = '';
	elementValue3Label = '';

	elementValue1Loading = false;
	elementValue2Loading = false;
	elementValue3Loading = false;

	elementValue1Unavailable = false;
	elementValue2Unavailable = false;
	elementValue3Unavailable = false;

	_id: string;

	reactionDisposers: IReactionDisposer[] = [];

	validationErrors?: RuleValidationErrors;

	dirty = false;

	abstract readonly elementType: T;
	abstract elementSubType1: number;
	abstract elementSubType2: number;
	abstract elementValue1: string | number | string[];
	abstract elementValue2: string | number;
	abstract elementValue3: string | number;

	abstract setElementSubType1(elementSubType1: number): void;
	abstract setElementSubType2(elementSubType2: number): void;
	abstract setElementValue1(elementValue1: number | string | string[]): void;
	abstract setElementValue2(elementValue1: number | string): void;
	abstract setElementValue3(elementValue1: number | string): void;

	get elementSubType1Options(): number[] {
		return [];
	}

	get elementSubType2Options(): number[] {
		return [];
	}

	constructor() {
		this._id = nanoid();
		makeObservable(this, {
			elementValue1Label: observable,
			elementValue1Loading: observable,
			elementValue1Unavailable: observable,
			elementValue2Label: observable,
			elementValue2Loading: observable,
			elementValue2Unavailable: observable,
			elementValue3Label: observable,
			elementValue3Loading: observable,
			elementValue3Unavailable: observable,
			setElementValue1Label: action,
			setElementValue1Loading: action,
			setElementValue1Unavailable: action,
			setElementValue2Label: action,
			setElementValue2Loading: action,
			setElementValue2Unavailable: action,
			setElementValue3Label: action,
			setElementValue3Loading: action,
			setElementValue3Unavailable: action,
			setValidationErrors: action,
			dirty: observable,
		});
	}

	dispose() {
		this.reactionDisposers.forEach((dispose) => dispose());
	}

	setValidationErrors(validationErrors?: RuleValidationErrors) {
		this.validationErrors = validationErrors;
	}

	setElementValue1Label(elementValue1Label: string) {
		this.elementValue1Label = elementValue1Label;
	}

	setElementValue2Label(elementValue2Label: string) {
		this.elementValue2Label = elementValue2Label;
	}

	setElementValue3Label(elementValue3Label: string) {
		this.elementValue3Label = elementValue3Label;
	}

	setElementValue1Loading(loading: boolean) {
		this.elementValue1Loading = loading;
	}

	setElementValue2Loading(loading: boolean) {
		this.elementValue2Loading = loading;
	}

	setElementValue3Loading(loading: boolean) {
		this.elementValue3Loading = loading;
	}

	setElementValue1Unavailable(unavailable: boolean) {
		this.elementValue1Unavailable = unavailable;
	}

	setElementValue2Unavailable(unavailable: boolean) {
		this.elementValue2Unavailable = unavailable;
	}

	setElementValue3Unavailable(unavailable: boolean) {
		this.elementValue3Unavailable = unavailable;
	}

	toRuleObject(): SearchRuleBase<T> | SearchRuleBase<T>[] {
		if (Array.isArray(this.elementValue1)) {
			return this.elementValue1.map((elementValue1) => ({
				elementType: this.elementType,
				elementSubType1: this.elementSubType1,
				elementSubType2: this.elementSubType2,
				elementValue1,
				elementValue2: this.elementValue2,
				elementValue3: this.elementValue3,
			})) as SearchRuleBase<T>[];
		}

		return {
			elementType: this.elementType,
			elementSubType1: this.elementSubType1,
			elementSubType2: this.elementSubType2,
			elementValue1: this.elementValue1,
			elementValue2: this.elementValue2,
			elementValue3: this.elementValue3,
		} as SearchRuleBase<T>;
	}
}

export default RuleModel;
